/**
 * useSelect hook for managing select input state with support for both single and multiple selection
 *
 * Features:
 * - Single and multiple selection modes
 * - Async option loading with requestOptions
 * - Search functionality with debouncing
 * - Form integration with validation
 * - Click outside to close
 *
 * @param props - SelectInputProps including multiple selection support
 * @returns Object with all necessary state and handlers for the select component
 */
import { requiredRule, useFormControl } from "@mongez/react-form";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import type { Option } from "~/design-system/types";
import { useClickOutside, useDebounce } from "~/shared/hooks";
import type { SelectInputProps } from "../components/form";

export function useSelect({
  options,
  requestOptions,
  searchKey,
  searchable,
  defaultValue,
  onChange,
  multiple = false,
  ...props
}: SelectInputProps) {
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsList, setOptionsList] = useState<Option[]>(options || []);
  const [openOptionsMenu, setOpenOptionsMenu] = useState(false);

  const getSelectedOptions = useCallback(() => {
    if (!defaultValue) return [];

    if (multiple && Array.isArray(defaultValue)) {
      return defaultValue
        .map(
          (value) =>
            optionsList?.find((option) => option.value === value) ||
            optionsList?.find((option) => option.label === value),
        )
        .filter(Boolean) as Option[];
    } else if (!multiple) {
      const option =
        optionsList?.find((option) => option.value === defaultValue) ||
        optionsList?.find((option) => option.label === defaultValue);
      return option ? [option] : [];
    }

    return [];
  }, [optionsList, defaultValue, multiple]);

  const [selectedOptions, setSelectedOptions] =
    useState<Option[]>(getSelectedOptions);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(optionsList);
  const [searchValue, setSearchValue] = useState("");
  const searchDebounce = useDebounce(searchValue, 500);

  const handleSearch = useCallback(() => {
    if (searchKey && requestOptions) {
      setLoadingOptions(true);
      requestOptions({ [searchKey]: searchDebounce })
        .then((options) => {
          setOptionsList(options);
          setFilteredOptions(options);
        })
        .catch((error) => toast.error(error.message))
        .finally(() => setLoadingOptions(false));
    } else if (optionsList) {
      setFilteredOptions(
        optionsList.filter((option) =>
          option.label
            ?.toString()
            ?.toLowerCase()
            .includes(searchDebounce.toLowerCase()),
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounce]);

  const { id, error, changeValue } = useFormControl({
    ...props,
    defaultValue: multiple
      ? getSelectedOptions().map((option) => option.value)
      : getSelectedOptions()[0]?.value,
    rules: [requiredRule],
  });

  const selectRef = useClickOutside(() => setOpenOptionsMenu(false));
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (openOptionsMenu && requestOptions) {
      setLoadingOptions(true);
      requestOptions()
        .then((options) => {
          setOptionsList(options);
          setFilteredOptions(options);
        })
        .catch((error) => toast.error(error.message))
        .finally(() => setLoadingOptions(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOptionsMenu]);

  useEffect(() => {
    if (searchable && searchDebounce) {
      handleSearch();
    } else {
      setFilteredOptions(optionsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounce, searchable]);

  const handleSelectOption = (option: Option) => {
    if (multiple) {
      const isSelected = selectedOptions.some(
        (selected) => selected.value === option.value,
      );
      let newSelectedOptions: Option[];

      if (isSelected) {
        // Remove option if already selected
        newSelectedOptions = selectedOptions.filter(
          (selected) => selected.value !== option.value,
        );
      } else {
        // Add option if not selected
        newSelectedOptions = [...selectedOptions, option];
      }

      setSelectedOptions(newSelectedOptions);
      changeValue(newSelectedOptions.map((option) => option.value));
      onChange?.(newSelectedOptions.map((option) => option.value));
    } else {
      // Single selection - close menu and set single option
      setSelectedOptions([option]);
      changeValue(option.value);
      setOpenOptionsMenu(false);
      onChange?.(option.value);
    }
  };

  const removeSelectedOption = (optionToRemove: Option) => {
    const newSelectedOptions = selectedOptions.filter(
      (option) => option.value !== optionToRemove.value,
    );
    setSelectedOptions(newSelectedOptions);
    changeValue(newSelectedOptions.map((option) => option.value));
    onChange?.(newSelectedOptions.map((option) => option.value));
  };

  const isOptionSelected = (option: Option) => {
    return selectedOptions.some((selected) => selected.value === option.value);
  };

  return {
    id,
    error,
    selectRef,
    searchRef,
    searchable,
    multiple,
    openOptionsMenu,
    setOpenOptionsMenu,
    filteredOptions,
    selectedOptions,
    searchValue,
    setSearchValue,
    handleSelectOption,
    removeSelectedOption,
    isOptionSelected,
    loadingOptions,
  };
}
